import { observer } from 'mobx-react';
import changeFrom from 'owa-mail-compose-store/lib/actions/changeFrom';
import initializeFromWellFindControlViewState from 'owa-mail-compose-store/lib/actions/initializeFromWellFindControlViewState';
import setIsFromEditing from 'owa-mail-compose-store/lib/actions/setIsFromEditing';
import toggleFromContextMenu from 'owa-mail-compose-store/lib/actions/toggleFromContextMenu';
import ComposeViewState from 'owa-mail-compose-store/lib/store/schema/ComposeViewState';
import { getSendAsMruAddresses } from 'owa-mail-compose-store/lib/utils/checkSendAsAddressAndAddToMru';
import handleIsFromValid from 'owa-mail-compose-store/lib/utils/handleIsFromValid';
import ReadOnlyRecipient from 'owa-readonly-recipient-well/lib/components/ReadOnlyRecipient';
import FindPeopleControl from 'owa-readwrite-recipient-well-fabric/lib/components/FindPeopleControl';
import isValidEmailAddress from 'owa-readwrite-recipient-well/lib/utils/isValidEmailAddress';
import FindRecipientPersonaType from 'owa-recipient-common/lib/store/schema/FindRecipientPersonaType';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import strings from 'owa-strings/owa-mail-strings';
import * as React from 'react';
import FromViewState, {
    LoadAliasActionStatus,
} from 'owa-mail-compose-store/lib/store/schema/FromViewState';
import {
    ContextualMenu,
    IContextualMenuItem,
    ContextualMenuItemType,
    DirectionalHint,
} from 'office-ui-fabric-react/lib/components/ContextualMenu';

const styles = require('./ComposeTop.scss');
const classNames = require('classnames/bind').bind(styles);

export interface ComposeFromMenuProps {
    fromViewState: FromViewState;
    composeViewState: ComposeViewState;
}

@observer
export default class ComposeFromMenu extends React.Component<ComposeFromMenuProps, {}> {
    private container: HTMLElement;
    private fromWellPeoplePicker: FindPeopleControl = null;

    constructor(props: ComposeFromMenuProps) {
        super(props);
    }

    render() {
        const {
            isFromContextMenuShown,
            from,
            isFromEditing,
            isFromValid,
            fromWellFindControlViewState,
        } = this.props.fromViewState;

        return (
            <div
                className={classNames(styles.recipientWell, styles.fromRecipientWellWrapper)}
                ref={ref => (this.container = ref)}>
                <div
                    tabIndex={0}
                    onClick={this.onToggleFromContextMenu}
                    className={styles.fromLabel}>
                    {strings.fromLabel}
                </div>
                {isFromContextMenuShown && (
                    <ContextualMenu
                        shouldFocusOnMount={true}
                        target={this.container}
                        directionalHint={DirectionalHint.bottomLeftEdge}
                        onDismiss={this.onToggleFromContextMenu}
                        items={this.fromItems}
                    />
                )}
                {isFromEditing ? (
                    <FindPeopleControl
                        ref={ref => {
                            this.fromWellPeoplePicker = ref;
                        }}
                        className={styles.fromWell}
                        viewState={fromWellFindControlViewState}
                        onItemSelected={this.onPersonSelected}
                        focusAutomatically={true}
                        openPickerAutomatically={true}
                        onBlur={this.onBlur}
                        onInputKeyDown={this.onKeyDown}
                    />
                ) : (
                    <ReadOnlyRecipient
                        key={from.email.EmailAddress}
                        viewState={from}
                        name={from.email.Name}
                        emailAddress={from.email.EmailAddress}
                        onlyShowEmail={true}
                        className={classNames(styles.fromAddress, {
                            isFromInvalid: !isFromValid,
                        })}
                        mailBoxType={from.email.MailboxType}
                        clientScenario="ComposeFromMenuReadOnlyRecipient"
                    />
                )}
            </div>
        );
    }

    getRootdiv() {
        return this.container;
    }

    private get fromItems(): IContextualMenuItem[] {
        const { fromViewState } = this.props;
        const { sendAsEmailAddresses, loadAliasActionStatus } = fromViewState;

        const menuItems = [];
        if (loadAliasActionStatus != LoadAliasActionStatus.Finished) {
            menuItems.push({
                name: strings.loading,
            });
        } else if (sendAsEmailAddresses && sendAsEmailAddresses.length > 0) {
            sendAsEmailAddresses.forEach(sendAsAddress => {
                menuItems.push({
                    name: sendAsAddress,
                    key: sendAsAddress,
                    onClick: this.onChangeFrom,
                });
            });

            // Add MRU for enterprise users
            if (!isConsumer()) {
                const mruAddresses = getSendAsMruAddresses();
                if (mruAddresses.length > 0) {
                    menuItems.push({
                        key: 'divider',
                        text: '-',
                        itemType: ContextualMenuItemType.Divider,
                    });

                    mruAddresses.forEach(mruAddress => {
                        menuItems.push({
                            name: mruAddress,
                            key: mruAddress,
                            onClick: this.onChangeFrom,
                        });
                    });
                }
            }

            menuItems.push({
                key: 'divider',
                text: '-',
                itemType: ContextualMenuItemType.Divider,
            });

            // Add menu item for inputting From address
            menuItems.push({
                name: strings.addFromAddressLabel,
                key: strings.addFromAddressLabel,
                onClick: this.onAddFrom,
            });
        }

        return menuItems;
    }

    private onToggleFromContextMenu = (ev: React.MouseEvent<EventTarget>) => {
        toggleFromContextMenu(this.props.fromViewState);
    };

    private onPersonSelected = (item: FindRecipientPersonaType) => {
        if (!item) {
            return;
        }

        const sendAs = item.EmailAddress;
        const sendAsAddress = sendAs ? sendAs.EmailAddress : item.primaryText;
        const sendAsName = sendAs ? sendAs.Name : item.primaryText;

        if (sendAsAddress && sendAsName) {
            changeFrom(this.props.fromViewState, sendAsAddress, sendAsName);

            const isFromValid = isValidEmailAddress(sendAsAddress);
            handleIsFromValid(this.props.composeViewState, this.props.fromViewState, isFromValid);

            if (this.fromWellPeoplePicker) {
                this.fromWellPeoplePicker.clearInput();
            }
        }
    };

    private onBlur = () => {
        if (this.fromWellPeoplePicker) {
            this.fromWellPeoplePicker.getFloatingPicker().forceResolveSuggestion();
        }
    };

    private onKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
        if (ev.key === 'Enter') {
            ev.preventDefault();
            ev.stopPropagation();
            this.onBlur();
        }
    };

    private onChangeFrom = (ev?: React.MouseEvent<EventTarget>, item?: IContextualMenuItem) => {
        changeFrom(this.props.fromViewState, item.key);
        handleIsFromValid(
            this.props.composeViewState,
            this.props.fromViewState,
            true /* isFromValid */
        );
    };

    private onAddFrom = (ev?: React.MouseEvent<EventTarget>) => {
        if (!this.props.fromViewState.fromWellFindControlViewState) {
            initializeFromWellFindControlViewState(this.props.fromViewState);
        }

        setIsFromEditing(this.props.fromViewState, true /* isFromEditing */);

        const fromWellPeoplePicker = this.fromWellPeoplePicker;
        if (fromWellPeoplePicker) {
            fromWellPeoplePicker.focus();
            fromWellPeoplePicker.showPicker();
        }
    };
}
